import React, { useEffect, useState } from "react";
import { Sparkles, Users, Smile, Hourglass, Zap, Gift, Coffee } from "lucide-react";
import { Button, Spinner } from "@/components/ui";
import { toast } from "sonner";
import { ReviewServices } from "@/services/bedrock";

interface ReviewResponseRefinementProps {
  reviewId: number | string;
  mode: "video" | "text";
  currentResponse: string;
  onRefined: (newResponse: string) => void;
  onRefining?: (isRefining: boolean) => void;
  isSubmitting?: boolean;
}

const REFINEMENT_SUGGESTIONS = [
  {
    label: "Mention team",
    prompt: "Acknowledge and thank our local team and office support for making this possible.",
    icon: <Users className="size-3.5 text-emerald-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Emphasize speed",
    prompt: "Highlight how fast and smooth we closed the transaction.",
    icon: <Zap className="size-3.5 text-amber-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Make it warmer",
    prompt: "Rewrite this in an ultra-warm, friendly, and appreciative tone.",
    icon: <Smile className="size-3.5 text-pink-500 shrink-0" />,
  },
  {
    label: "Keep it short",
    prompt: "Make the response significantly shorter, punchier, and easier to read.",
    icon: <Hourglass className="size-3.5 text-blue-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Ask for referrals",
    prompt: "Add a polite and warm invitation asking them to keep us in mind for their friends and family.",
    icon: <Gift className="size-3.5 text-purple-500 shrink-0 mt-0.5" />,
  },
  {
    label: "Grab coffee",
    prompt: "Invite them to reach out anytime and offer to treat them to coffee next time they are in the area.",
    icon: <Coffee className="size-3.5 text-amber-600 shrink-0 mt-0.5" />,
  },
];

export function ReviewResponseRefinement({
  reviewId,
  mode,
  currentResponse,
  onRefined,
  onRefining,
  isSubmitting = false,
}: ReviewResponseRefinementProps) {
  const [isRefining, setIsRefining] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);

  useEffect(() => {
    onRefining?.(isRefining);
  }, [isRefining, onRefining]);

  const handleToggleSuggestion = (label: string) => {
    setSelectedSuggestions(prev => (prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]));
  };

  const handleApplyRefinements = async () => {
    if (!reviewId) {
      toast.error("Review context is missing.");
      return;
    }
    setIsRefining(true);
    try {
      const activePrompts = REFINEMENT_SUGGESTIONS.filter(s => selectedSuggestions.includes(s.label)).map(
        s => s.prompt
      );

      const combinedInstructions = [...activePrompts, customInstructions.trim()].filter(Boolean).join(" ");

      const apiCall =
        mode === "video"
          ? ReviewServices.generateResponseScript(reviewId, {
              userInstructions: combinedInstructions || undefined,
              previousResponse: currentResponse,
              force: true,
            })
          : ReviewServices.generateResponseText(reviewId, {
              userInstructions: combinedInstructions || undefined,
              previousResponse: currentResponse,
              force: true,
            });

      const result = await apiCall;
      const refined = result
        ? "script" in result
          ? result.script
          : "response" in result
          ? result.response
          : undefined
        : undefined;
      if (refined) {
        onRefined(refined);
        toast.success("Response refined successfully!");
        setCustomInstructions("");
        setSelectedSuggestions([]);
      } else {
        toast.error("Failed to refine response.");
      }
    } catch (error) {
      console.error("Refining response failed:", error);
      toast.error("An error occurred while refining.");
    } finally {
      setIsRefining(false);
    }
  };

  const isLoadingAny = isRefining || isSubmitting;

  return (
    <div className="flex flex-col gap-4 relative">
      {isRefining && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex flex-col items-center justify-center rounded-xl z-20">
          <Spinner className="size-6 text-primary animate-spin mb-2" />
          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Refining...</span>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-base font-bold text-slate-900">
        Refine with
        <img alt={"SocialCoach AI"} src="https://cdn.socialcoach.io/socoai_mono_positive.svg" className="h-[12px]" />
      </div>

      {/* Predefined suggestions */}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold text-slate-500">Tone & Content Suggestions:</span>
        <div className="grid grid-cols-2 gap-2">
          {REFINEMENT_SUGGESTIONS.map(suggestion => {
            const isSelected = selectedSuggestions.includes(suggestion.label);
            return (
              <button
                key={suggestion.label}
                type="button"
                disabled={isLoadingAny}
                onClick={() => handleToggleSuggestion(suggestion.label)}
                className={`px-2 py-2 text-left text-xs font-bold border rounded-lg transition-all duration-200 flex items-start align-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none leading-tight cursor-pointer ${
                  isSelected
                    ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                    : "bg-slate-50 hover:bg-indigo-50 hover:text-indigo-600 border-slate-200 hover:border-indigo-200 text-slate-700"
                }`}
              >
                {suggestion.icon}
                <span>{suggestion.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom instructions & Refinement Button */}
      <div className="flex flex-col gap-3 mt-auto pt-2">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-500">Custom instructions (optional):</span>
          <input
            type="text"
            placeholder="Add a personal touch..."
            value={customInstructions}
            onChange={e => setCustomInstructions(e.target.value)}
            disabled={isLoadingAny}
            className="w-full text-xs px-3 py-2 h-9 border border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg outline-none font-medium placeholder:text-slate-400 disabled:opacity-50 transition-all bg-white"
            onKeyDown={e => {
              if (e.key === "Enter" && (customInstructions.trim() || selectedSuggestions.length > 0)) {
                handleApplyRefinements();
              }
            }}
          />
        </div>

        <Button
          type="button"
          variant="outline"
          disabled={isLoadingAny || (!customInstructions.trim() && selectedSuggestions.length === 0)}
          onClick={handleApplyRefinements}
          className="w-full h-10 flex items-center justify-center gap-1.5 border-primary border-2 text-primary hover:bg-primary/5"
        >
          <Sparkles className="size-4 animate-pulse" />
          {isRefining ? "Refining..." : "Refine Response with AI"}
        </Button>
      </div>
    </div>
  );
}
